import { addDays, format, parseISO, subDays } from 'date-fns';
import { AUDIENCES_MOCK } from './audiences.mock';
import { DASHBOARDS_BY_AUDIENCE, DEFAULT_DASHBOARD } from './dashboard.mock';
import { SOURCES_MOCK } from './sources.mock';
import { Audience, DashboardFilter, DashboardResponse, DailyPoint, SourcesResponse } from './types';

const delay = <T>(value: T, ms = 120): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), ms));

export const getAudiences = (): Promise<Audience[]> => delay(AUDIENCES_MOCK);

export const getSources = (): Promise<SourcesResponse> => delay(SOURCES_MOCK);

const sumGoogle = (totalsByAcc: number, selectedCount: number, allCount: number): number =>
  allCount === 0 ? 0 : Math.round((totalsByAcc / allCount) * selectedCount);

const sumCost = (total: number, selectedCount: number, allCount: number): number => {
  if (allCount === 0) return 0;
  return Math.round((total / allCount) * selectedCount * 100) / 100;
};

const expandDaily = (base: DailyPoint[], fromIso: string, toIso: string): DailyPoint[] => {
  const from = parseISO(fromIso);
  const to = parseISO(toIso);
  const dayCount = Math.max(1, Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1);

  // Sort base by date to identify the 7-day pattern
  const baseByDate: Record<string, DailyPoint[]> = {};
  for (const p of base) {
    const k = p.date;
    if (!baseByDate[k]) baseByDate[k] = [];
    baseByDate[k]!.push(p);
  }
  const baseDates = Object.keys(baseByDate).sort();
  const pattern = baseDates.map((d) => baseByDate[d]!);

  const out: DailyPoint[] = [];
  for (let i = 0; i < dayCount; i++) {
    const d = format(addDays(from, i), 'yyyy-MM-dd');
    const tile = pattern[i % pattern.length]!;
    for (const tp of tile) {
      out.push({ ...tp, date: d });
    }
  }
  return out;
};

export const getDashboardData = (filter: DashboardFilter): Promise<DashboardResponse> => {
  const base = DASHBOARDS_BY_AUDIENCE[filter.audienceId] ?? DEFAULT_DASHBOARD;

  const googleSelected = filter.googleAccountIds?.length ?? 1;
  const metaSelected = filter.metaAccountIds?.length ?? 1;
  const googleAll = 1; // single real Google Ads account (Enrico Personal)
  const metaAll = 1; // single real Meta ad account (Polimo Ads)

  // Recompute totals proportionally to selected accounts
  const platforms = base.platforms.map((p) => {
    if (p.platform === 'google') {
      return {
        ...p,
        impressions: sumGoogle(p.impressions, googleSelected, googleAll),
        clicks: sumGoogle(p.clicks, googleSelected, googleAll),
        cost: sumCost(p.cost, googleSelected, googleAll),
        storeVisits: p.storeVisits !== null ? sumGoogle(p.storeVisits, googleSelected, googleAll) : null,
      };
    }
    return {
      ...p,
      impressions: sumGoogle(p.impressions, metaSelected, metaAll),
      clicks: sumGoogle(p.clicks, metaSelected, metaAll),
      cost: sumCost(p.cost, metaSelected, metaAll),
      reach: p.reach !== null ? sumGoogle(p.reach, metaSelected, metaAll) : null,
    };
  });

  let daily = base.daily.map((d) => {
    if (d.platform === 'google') {
      return {
        ...d,
        impressions: sumGoogle(d.impressions, googleSelected, googleAll),
        clicks: sumGoogle(d.clicks, googleSelected, googleAll),
        cost: sumCost(d.cost, googleSelected, googleAll),
      };
    }
    return {
      ...d,
      impressions: sumGoogle(d.impressions, metaSelected, metaAll),
      clicks: sumGoogle(d.clicks, metaSelected, metaAll),
      cost: sumCost(d.cost, metaSelected, metaAll),
    };
  });

  // Expand/contract daily series to the chosen range
  daily = expandDaily(daily, filter.from, filter.to);

  // Re-aggregate totals if the date range is not 7-day default
  const baseRangeDays = 7;
  const requestedDays = Math.max(1, Math.round((parseISO(filter.to).getTime() - parseISO(filter.from).getTime()) / (1000 * 60 * 60 * 24)) + 1);
  if (requestedDays !== baseRangeDays) {
    const ratio = requestedDays / baseRangeDays;
    for (const p of platforms) {
      p.impressions = Math.round(p.impressions * ratio);
      p.clicks = Math.round(p.clicks * ratio);
      p.cost = Math.round(p.cost * ratio * 100) / 100;
      if (p.reach !== null) p.reach = Math.round(p.reach * Math.min(ratio, 1.5));
      if (p.storeVisits !== null) p.storeVisits = Math.round(p.storeVisits * ratio);
    }
  }

  return delay({
    ...base,
    range: { from: filter.from, to: filter.to },
    audienceId: filter.audienceId,
    platforms,
    daily,
  });
};

export const computeDefaultRange = (preset: string): { from: string; to: string } => {
  // The "now" anchor: latest date present in the default dashboard
  const anchor = parseISO('2026-06-01');
  switch (preset) {
    case 'Oggi':
      return { from: format(anchor, 'yyyy-MM-dd'), to: format(anchor, 'yyyy-MM-dd') };
    case 'Ieri': {
      const d = subDays(anchor, 1);
      return { from: format(d, 'yyyy-MM-dd'), to: format(d, 'yyyy-MM-dd') };
    }
    case 'Ultimi 7 giorni':
      return { from: format(subDays(anchor, 6), 'yyyy-MM-dd'), to: format(anchor, 'yyyy-MM-dd') };
    case 'Ultimi 14 giorni':
      return { from: format(subDays(anchor, 13), 'yyyy-MM-dd'), to: format(anchor, 'yyyy-MM-dd') };
    case 'Ultimi 30 giorni':
      return { from: format(subDays(anchor, 29), 'yyyy-MM-dd'), to: format(anchor, 'yyyy-MM-dd') };
    case 'Mese corrente':
      return { from: '2026-06-01', to: format(anchor, 'yyyy-MM-dd') };
    case 'Mese scorso':
      return { from: '2026-05-01', to: '2026-05-31' };
    case 'Trimestre corrente':
      return { from: '2026-04-01', to: format(anchor, 'yyyy-MM-dd') };
    default:
      return { from: format(subDays(anchor, 6), 'yyyy-MM-dd'), to: format(anchor, 'yyyy-MM-dd') };
  }
};
